/**
 * The list that opens when "@" is typed in an update: the account's users and
 * teams, filtered by what was typed after the "@", plus "Applier" — the
 * dynamic mention that resolves to whoever applies the template.
 */
import { Loader2 } from 'lucide-react';
import React from 'react';
import { APPLIER_MENTION } from '../../lib/updates';
import AnchoredPopover from './AnchoredPopover';

const MAX_ROWS = 30;

export default function MentionPicker({ anchorEl, query, people, onPick, onClose }) {
  const q = String(query || '').trim().toLowerCase();
  const matches = (name) => !q || String(name || '').toLowerCase().includes(q);
  const users = (people.users || []).filter((u) => matches(u.name)).slice(0, MAX_ROWS);
  const teams = (people.teams || []).filter((t) => matches(t.name)).slice(0, MAX_ROWS);
  const applierMatches = matches(APPLIER_MENTION.name) || 'whoever applies'.includes(q);
  const loading = people.status !== 'loaded';

  return (
    <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={280}>
      <div className="spt-menu-caption">Mention</div>
      <div className="spt-menu-list" aria-label="People and teams to mention">
        {applierMatches && (
          <button type="button" className="spt-menu-row" onClick={() => onPick(APPLIER_MENTION)}>
            <span className="spt-menu-row-title">{APPLIER_MENTION.name}</span>
            <span className="spt-menu-row-type">whoever applies</span>
          </button>
        )}
        {loading && (
          <div className="spt-menu-empty inline-flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Loading people and teams
          </div>
        )}
        {!loading && people.error && (
          <div className="spt-menu-empty">
            Could not load people. monday said: “{people.error}”. If this mentions missing scopes,
            add <code>users:read</code> and <code>teams:read</code> to the app's permissions.
          </div>
        )}
        {!loading && !people.error && !users.length && !teams.length && (
          <div className="spt-menu-empty">No one matches “{query}”.</div>
        )}
        {users.map((u) => (
          <button
            key={`u${u.id}`}
            type="button"
            className="spt-menu-row"
            onClick={() => onPick({ id: u.id, type: 'User', name: u.name })}
          >
            <span className="spt-menu-row-title">{u.name}</span>
            <span className="spt-menu-row-type">user</span>
          </button>
        ))}
        {teams.map((t) => (
          <button
            key={`t${t.id}`}
            type="button"
            className="spt-menu-row"
            onClick={() => onPick({ id: t.id, type: 'Team', name: t.name })}
          >
            <span className="spt-menu-row-title">{t.name}</span>
            <span className="spt-menu-row-type">team</span>
          </button>
        ))}
      </div>
    </AnchoredPopover>
  );
}
